import { MouseEvent } from 'react';
import { useReducedMotion } from 'framer-motion';
// @mui
import { Box } from '@mui/material';
//
import { YEAR } from '../../config-global';
import { Product } from '../../data/products';
import { CONTAINER, STAGE, TYPE } from '../../theme/stage';
import {
  ANCHOR,
  FOOTER,
  NEXT_PRODUCT,
  STATUS_NAME,
  copyright,
  productLinkAriaLabel,
  productNumber,
} from './copy';
import Accent from './primitives/Accent';
import ActionLink from './primitives/ActionLink';
import Rich from './primitives/Rich';
import TwoTone from './primitives/TwoTone';
import { focusMain } from './SkipLink';

// ----------------------------------------------------------------------

type Props = {
  products: Product[];
};

const BAND_ID = 'footer-band';

const rule = `1px solid ${STAGE.line}`;

const HEADING = { ...TYPE.label, m: '0 0 8px', color: STAGE.mute };

const LIST = { listStyle: 'none', m: 0, p: 0 };

const LINK = {
  fontWeight: 400,
  fontSize: 16,
  justifyContent: 'flex-start',
  color: STAGE.ink2,
};

const NOTE = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  minHeight: 44,
  m: 0,
  fontSize: { xs: 15, sm: 16 },
  lineHeight: 1.25,
};

function LinkList({ links }: { links: { label: string; href: string }[] }) {
  return (
    <Box component="ul" sx={LIST}>
      {links.map(({ label, href }) => (
        <li key={href}>
          <ActionLink variant="quiet" href={href} sx={LINK}>
            {label}
          </ActionLink>
        </li>
      ))}
    </Box>
  );
}

export default function SiteFooter({ products }: Props) {
  const reduced = useReducedMotion();

  const next = products[products.length - 1].index + 1;

  const toTop = (event: MouseEvent) => {
    event.preventDefault();
    window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
    focusMain();
  };

  return (
    <Box
      component="footer"
      data-chrome
      sx={{ borderTop: rule, bgcolor: STAGE.ground, color: STAGE.mute }}
    >
      <Box sx={CONTAINER}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'flex-end' },
            justifyContent: 'space-between',
            gap: { xs: '28px', md: '48px' },
            pt: { xs: '72px', md: '120px' },
            pb: { xs: '56px', md: '72px' },
            borderBottom: rule,
          }}
        >
          <TwoTone
            as="h2"
            id={BAND_ID}
            lines={FOOTER.band.lines}
            size="billboard"
            lineMode="block"
            enter="view"
            sx={{
              maxWidth: '15em',
              fontSize: { xs: 'clamp(32px, 4.5vw, 64px)' },
              lineHeight: { xs: 1.06 },
              letterSpacing: { xs: '-0.03em' },
            }}
          />
          <ActionLink
            variant="pill"
            icon="forward"
            href={FOOTER.band.cta.href}
            sx={{ flexShrink: 0 }}
          >
            {FOOTER.band.cta.label}
          </ActionLink>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(4, minmax(0, 1fr))',
            },
            columnGap: { xs: '8px', md: '24px' },
            rowGap: '40px',
            py: { xs: '48px', md: '56px' },
          }}
        >
          <Box
            component="nav"
            aria-label={FOOTER.navLabel}
            sx={{
              display: 'grid',
              gridTemplateColumns: 'subgrid',
              gridTemplateRows: { xs: 'subgrid', md: 'none' },
              gridColumn: { xs: '1 / -1', md: '1 / 4' },
              gridRow: { xs: '1 / span 2', md: '1' },
            }}
          >
            <div>
              <Box component="h2" sx={HEADING}>
                {FOOTER.headings.products}
              </Box>
              <Box component="ul" sx={LIST}>
                {products.map((product) => (
                  <li key={product.id}>
                    <ActionLink
                      variant="quiet"
                      href={product.url}
                      ariaLabel={productLinkAriaLabel(product.name, new URL(product.url).hostname)}
                      sx={LINK}
                    >
                      {product.name}
                    </ActionLink>
                  </li>
                ))}
                <li>
                  <Box component="p" sx={NOTE}>
                    {productNumber(next)} · {NEXT_PRODUCT.status}
                  </Box>
                </li>
              </Box>
            </div>

            <div>
              <Box component="h2" sx={HEADING}>
                {FOOTER.headings.company}
              </Box>
              <LinkList links={FOOTER.company} />
            </div>

            <div>
              <Box component="h2" sx={HEADING}>
                {FOOTER.headings.legal}
              </Box>
              <LinkList links={FOOTER.legal} />
            </div>
          </Box>

          <Box sx={{ gridColumn: { xs: '2', md: '4' }, gridRow: { xs: '2', md: '1' } }}>
            <Box component="h2" sx={HEADING}>
              {FOOTER.headings.status}
            </Box>
            {products.map((product) => (
              <Box key={product.id} component="p" sx={{ ...NOTE, color: STAGE.ink2 }}>
                <Box
                  component="span"
                  aria-hidden
                  sx={{
                    flexShrink: 0,
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: product.brand.main,
                  }}
                />
                {product.name} · {STATUS_NAME[product.status]}
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            columnGap: '24px',
            pt: '8px',
            pb: { xs: '40px', md: '48px' },
            borderTop: rule,
          }}
        >
          <Box component="p" sx={{ ...TYPE.fine, m: 0, fontVariantNumeric: 'tabular-nums' }}>
            {copyright(YEAR)} <Rich parts={FOOTER.registration} />
          </Box>
          <Box component="p" sx={{ m: 0, fontSize: 15, lineHeight: '44px' }}>
            <Accent tone="mute" opsz={14}>
              {FOOTER.signOff}
            </Accent>
          </Box>
          <ActionLink
            variant="quiet"
            icon="up"
            href={`#${ANCHOR.main}`}
            onClick={toTop}
            sx={{ fontSize: 13, justifyContent: 'flex-start' }}
          >
            {FOOTER.backToTop}
          </ActionLink>
        </Box>
      </Box>
    </Box>
  );
}
